import { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumbs as MuiBreadcrumbs, Link, SxProps, Typography, Skeleton } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { blue } from '@mui/material/colors';

interface BreadcrumbsProps {
  idReplacement?: string;
  nestedIdReplacement?: string;
  loading?: boolean;
  sx?: SxProps;
}

export const Breadcrumbs: FC<BreadcrumbsProps> = ({ idReplacement, nestedIdReplacement, loading, sx }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathnames = location.pathname.split('/').filter((x) => x);
  
  const isNestedIdRoute = pathnames.length >= 4 && 
    ((pathnames[1] === 'posts' || pathnames[1] === 'jobs' || pathnames[1] === 'campaigns') && 
     !isNaN(Number(pathnames[2])) && !isNaN(Number(pathnames[3]))) ||
    (pathnames[1] === 'candidates' && !isNaN(Number(pathnames[2])) && pathnames[3] === 'resume');

  return (
    <MuiBreadcrumbs 
      separator={<ChevronRightIcon fontSize="small" />}
      aria-label="breadcrumb" 
      sx={{ py: 2, px: 4, ...sx }}
    >
      {pathnames.map((name, index) => {
        let displayName = name;
        
        if (displayName === 'employer' && index !== 0) {
          displayName = 'employers';
        } else if (displayName === 'jobseeker' && index !== 0) {
          displayName = 'jobseekers';
        } else if (displayName === 'rooftop') {
          displayName = 'rooftops';
        } else if (displayName === 'job') {
          displayName = 'jobs';
        } else if (displayName === 'campaign') {
          displayName = 'campaigns';
        } else if (displayName === 'post') {
          displayName = 'posts';
        }

        if (isNestedIdRoute) {
          if (pathnames[1] === 'candidates' && index === 2 && idReplacement) {
            displayName = idReplacement;
          } else if (pathnames[1] !== 'candidates' && index === 2 && nestedIdReplacement) {
            displayName = nestedIdReplacement;
          }
        }
        
        const routeTo = `/${pathnames.slice(0, index + 1).map((segment, i) => {
          if (i === index) {
            if (segment === 'employer' && index !== 0) return 'employers';
            if (segment === 'jobseeker' && index !== 0) return 'jobseekers';
            if (segment === 'rooftop') return 'rooftops';
            if (segment === 'job') return 'jobs';
            if (segment === 'campaign') return 'campaigns';
            if (segment === 'post') return 'posts';
          }
          return segment;
        }).join('/')}`;
        
        const isLast = index === pathnames.length - 1;
        
        if (isLast && !loading) {
          if (pathnames[1] === 'candidates' && pathnames[3] === 'resume') {
            return (
              <Typography key={name} color="text.primary" variant="caption">
                Resume
              </Typography>
            );
          }
          return idReplacement ? (
            <Typography key={name} color="text.primary" variant="caption">
              {idReplacement}
            </Typography>
          ) : (
            <Typography key={name} color="text.primary" variant="caption">
              {displayName.charAt(0).toUpperCase() + displayName.slice(1)}
            </Typography>
          );
        }
        
        return loading ? (
          <Skeleton key={name} variant="text" width={isLast ? 100 : 50} />
        ) : (
          <Link
            key={name}
            component="button"
            variant="caption"
            onClick={() => navigate(routeTo)}
            sx={{ 
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
                color: blue[300]
              }
            }}
          >
            {displayName.charAt(0).toUpperCase() + displayName.slice(1)}
          </Link>
        );
      })}
    </MuiBreadcrumbs>
  );
};